import React from 'react';
import { WhatsAppConversation } from '@/services/integrations/whatsappInboxService';
import {
  getInitials, avatarColorFor, formatRelativeTime, sessionWindowInfo,
  LEAD_BUCKET_BADGE, CONVERSATION_STATUS_BADGE,
} from '@/utils/whatsappInbox';
import { displayName } from '@/utils/displayName';

interface ConversationListItemProps {
  conversation: WhatsAppConversation;
  active: boolean;
  onClick: () => void;
}

const ConversationListItem: React.FC<ConversationListItemProps> = ({ conversation: c, active, onClick }) => {
  // Cleaned once so the label, the initials and the avatar colour all agree —
  // 'None mahender kumar' would otherwise initial as 'NM' instead of 'MK'.
  const name = displayName(c.lead_name);
  const avatar = avatarColorFor(c.wa_phone || name || String(c.id));
  const hasUnread = c.unread_count > 0;
  const bucket = c.lead_bucket ? LEAD_BUCKET_BADGE[c.lead_bucket] : null;
  const statusBadge = c.status !== 'open' ? CONVERSATION_STATUS_BADGE[c.status] : null;
  const win = c.status === 'open' && !c.opted_out ? sessionWindowInfo(c.last_inbound_at) : null;
  // How long the customer has been waiting for our reply (they wrote last).
  const waitingFor = (() => {
    if (!c.awaiting_reply || !c.last_inbound_at) return null;
    const mins = Math.max(0, Math.floor((Date.now() - new Date(c.last_inbound_at).getTime()) / 60000));
    if (mins < 60) return `${mins}m`;
    if (mins < 48 * 60) return `${Math.floor(mins / 60)}h`;
    return `${Math.floor(mins / 1440)}d`;
  })();

  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex gap-2.5 px-4 py-3 border-b border-gray-100 transition-colors ${
        active
          ? 'bg-[#eef5f0]'
          : hasUnread ? 'bg-white hover:bg-gray-50' : 'bg-white hover:bg-gray-50 opacity-90'
      }`}
    >
      <div className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center text-[13px] font-bold ${avatar.bg} ${avatar.text}`}>
        {getInitials(name || c.wa_phone)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <div className={`text-[13.5px] flex-1 min-w-0 truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
            {name || c.wa_phone || 'Unknown'}
          </div>
          <div className={`text-[10.5px] flex-shrink-0 font-semibold ${hasUnread ? 'text-[#153d23]' : 'text-gray-400'}`}>
            {formatRelativeTime(c.last_message_at)}
          </div>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 mb-1">
          <div className="text-[11px] text-gray-500 flex-1 min-w-0 truncate">{c.wa_phone}</div>
          {hasUnread && (
            <span className="min-w-[18px] h-[18px] flex-shrink-0 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold flex items-center justify-center px-1">
              {c.unread_count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          {bucket && (
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${bucket.bg} ${bucket.text}`}>
              {bucket.label}
            </span>
          )}
          {statusBadge && (
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border || ''}`}>
              {statusBadge.label}
            </span>
          )}
          {c.opted_out && (
            <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-700">Opted out</span>
          )}
          {waitingFor && (
            <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"
                  title="The customer wrote last and is waiting for our reply">
              Waiting {waitingFor}
            </span>
          )}
          {win && (
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
              win.open ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'
            }`}>
              {win.open ? win.label : 'Window closed'}
            </span>
          )}
          {c.owner_name && <span className="text-[11px] text-gray-500 font-semibold">{c.owner_name}</span>}
        </div>
      </div>
    </button>
  );
};

export default ConversationListItem;
